import PredictiveSearch from './PredictiveSearch';
import { SEARCH_PANEL_OFFSET_EVENT } from '../../constants/header';
import { debounce } from '../../helpers';
import initBuyButtons from '../../global/initBuyButtons';
import initCarousel from '../../helpers/carousel';
import { reinitSwymActionButtons } from '../../helpers';
import { unitlessBreakpoints } from '../../helpers/breakpoints';
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';

const selectors = {
  searchInput: '[data-search-input]',
  searchForm: '[data-search-form]',
  searchClear: '[data-search-clear]',
  searchClose: '[data-search-close]',
  searchPopout: '[data-search-popout]',
  searchBackdrop: '[data-search-backdrop]',
  results: '#predictive-search',
  defaultResults: '[data-search-default]',
  searchResults: '[data-search-results]',
};

export default class Search {
  constructor(element) {
    this.isResultHidden = true;
    this.isScrollLocked = false;

    this.searchInput = element.el.querySelector(selectors.searchInput);
    this.searchForm = element.el.querySelector(selectors.searchForm);
    this.searchClear = element.el.querySelector(selectors.searchClear);
    this.searchClose = element.el.querySelector(selectors.searchClose);
    this.searchPopout = element.el.querySelector(selectors.searchPopout);
    this.searchBackdrop = element.el.querySelector(selectors.searchBackdrop);
    this.results = element.el.querySelector(selectors.results);

    if (!this.searchInput || !this.searchForm || !this.results) return;

    this.defaultResultsMarkup = this.results.innerHTML.trim();
    this.hasDefaultResults = Boolean(this.defaultResultsMarkup);

    this.predictiveSearch = new PredictiveSearch('predictive-search', {
      limit: 8,
      limit_scope: 'each',
      fields: 'body,product_type,tag,title,variants.sku,variants.title,vendor',
    });

    this._offFocus = this._offFocus.bind(this);
    this._onActivate = this._onActivate.bind(this);
    this._openResults = this._openResults.bind(this);
    this._closeResults = this._closeResults.bind(this);
    this._handleKeydown = this._handleKeydown.bind(this);
    this._onClear = this._onClear.bind(this);
    this._onClose = this._onClose.bind(this);
    this._onInputValueChange = this._onInputValueChange.bind(this);

    this._initEventListeners();
    this._updateActionButtons();

    if (this.hasDefaultResults) {
      this._initSearchCarousel();
    }
  }

  _initEventListeners() {
    document.addEventListener('click', this._offFocus);
    document.addEventListener('keydown', this._handleKeydown);

    this.searchInput.addEventListener('input', this._onInputValueChange);
    this.searchInput.addEventListener(
      'input',
      debounce(this._onInput.bind(this), 300),
    );
    this.searchInput.addEventListener('focus', this._onActivate);
    this.searchClear?.addEventListener('click', this._onClear);
    this.searchClose?.addEventListener('click', this._onClose);
    this.searchBackdrop?.addEventListener('click', this._onClose);
  }

  // Close the results when clicking outside the search UI
  _offFocus(event) {
    if (this.searchForm.contains(event.target)) return;
    if (this.results.contains(event.target)) return;
    if (this.searchBackdrop?.contains(event.target)) return;

    if (!this.isResultHidden) {
      this._closeResults();
    }
  }

  _handleKeydown(event) {
    if (this.isResultHidden || event.key !== 'Escape') return;

    this._closeResults();
    this.searchInput.blur();
    this._updateActionButtons();
  }

  _onActivate() {
    this._updateActionButtons();

    if (!this.isResultHidden) return;

    const searchTerm = this.searchInput.value.trim();

    if (searchTerm.length) {
      this._fetchResults(searchTerm);
      return;
    }

    this._showDefaultResults();
    this._openResults();
  }

  _onInputValueChange() {
    this._updateActionButtons();
  }

  _onClear(event) {
    event.preventDefault();
    this.searchInput.value = '';
    this.searchInput.focus();
    this._updateActionButtons();
    this._showDefaultResults();
    this._openResults();
  }

  _onClose(event) {
    event.preventDefault();
    this._closeResults();
    this.searchInput.blur();
    this._updateActionButtons();
  }

  _onInput() {
    const searchTerm = this.searchInput.value.trim();

    if (!searchTerm.length) {
      this._showDefaultResults();
      this._openResults();
      return;
    }

    this._fetchResults(searchTerm);
  }

  _updateActionButtons() {
    const isActive = !this.isResultHidden || document.activeElement === this.searchInput;
    const hasValue = this.searchInput.value.length > 0;

    if (this.searchClose) {
      this.searchClose.hidden = !isActive;
    }

    if (this.searchClear) {
      this.searchClear.hidden = !(isActive && hasValue);
    }
  }

  _fetchResults(searchTerm) {
    this.predictiveSearch
      .query(searchTerm)
      .then(response => {
        if (!response.ok) {
          const error = new Error(response.status);
          this._closeResults();
          throw error;
        }

        return response.text();
      })
      .then(text => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.results.innerHTML = resultsMarkup;
        this._initSearchCarousel();
        this._openResults();
      })
      .catch(error => {
        this._closeResults();
        throw error;
      });
  }

  _showDefaultResults() {
    if (!this.hasDefaultResults) return;
    if (this.results.querySelector(selectors.defaultResults)) return;

    this.results.innerHTML = this.defaultResultsMarkup;
    this._initSearchCarousel();
  }

  _initSearchCarousel() {
    if (!this.results.querySelector('[data-splide-carousel]')) return;

    initCarousel(this.results, {
      pagination: false,
      perPage: 1,
      breakpoints: {
        [unitlessBreakpoints.xs]: {
          perPage: 2,
        },
        [unitlessBreakpoints.md]: {
          perPage: 3,
        },
        [unitlessBreakpoints.xl]: {
          perPage: 4,
        },
      },
    });
    initBuyButtons(this.results);
    reinitSwymActionButtons(this.results);
  }

  _openResults() {
    if (!this.results.innerHTML.trim()) return;

    this.searchForm.closest('header')?.classList.remove('-translate-y-full');
    document.dispatchEvent(new CustomEvent(SEARCH_PANEL_OFFSET_EVENT));

    this.isResultHidden = false;
    if (this.searchPopout) this.searchPopout.hidden = false;
    this.searchInput.setAttribute('aria-expanded', 'true');
    this._updateScrollLock();
    this._updateActionButtons();
  }

  _closeResults() {
    this.isResultHidden = true;
    if (this.searchPopout) this.searchPopout.hidden = true;
    this.searchInput.setAttribute('aria-expanded', 'false');
    this._updateScrollLock();
    this._updateActionButtons();
  }

  _updateScrollLock() {
    const shouldLock = !this.isResultHidden;

    if (shouldLock === this.isScrollLocked) return;

    if (shouldLock) {
      disablePageScroll();
    } else {
      enablePageScroll();
    }

    this.isScrollLocked = shouldLock;
  }
}
